import { useCurrentClientIfAvailable } from 'src/clients/context/CurrentClientProvider';
import PageLoader from 'src/UI/loader/PageLoader';
import { useNavigate, useParams } from 'react-router-dom';
import React from 'react';
import { useEntity } from 'src/financial/financial-data/hooks/useFinancialItem';
import { GenericErrorPage } from 'src/error/GenericErrorPage';
import { Client } from 'src/users';
import { FinancialItemDataDynamicForm } from 'src/financial/financial-data/financial-item/FinancialItemDataDynamicForm';
import { useDeleteEntities } from 'src/data-capture/merge-review/useDeleteEntities';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';

type Params = {
  id: string;
  product: string;
  clientId: string;
};

export default function ViewAndEditFinancialItemPage() {
  const { product, id } = useParams<Params>();
  const { client } = useCurrentClientIfAvailable();

  if (product && id && client) {
    return <ViewAndEditFinancialItem product={product} id={id} client={client} />;
  }

  return null;
}

type Props = {
  id: string;
  product: string;
  client: Client;
};

const ViewAndEditFinancialItem = ({ product, id, client }: Props) => {
  const {
    isLoading: isLoadingEntityData,
    refetch: refetchEntityData,
    entityData,
    error,
    isError,
  } = useEntity({ id, clientId: client.id, entityType: product });

  const navigate = useNavigate();

  const { deleteEntityByTypeAndId, isLoading: isDeleting } = useDeleteEntities();

  const isLoading = isLoadingEntityData || !entityData || isDeleting;

  if (isError) {
    return <GenericErrorPage error={error} />;
  }

  const handleDeletePress = async () => {
    try {
      await deleteEntityByTypeAndId(client.id, product, id);
      navigate(`/client/${client.id}/financial-data`);
    } catch (e) {
      notifyAndLogError(
        'Error deleting item',
        'Something went wrong trying to delete the item. Please try again and contact support if the issue persists.',
        e,
        { product, id },
      );
    }
  };

  const handleSuccessfulSave = async () => {
    await refetchEntityData();
  };

  return isLoading ? (
    <PageLoader />
  ) : (
    <FinancialItemDataDynamicForm
      product={product}
      client={client}
      isCreateMode={false}
      entityWithMetadata={entityData}
      onDeleteConfirm={handleDeletePress}
      onSuccessfulSave={handleSuccessfulSave}
    />
  );
};
